import { Component, EventEmitter, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    Discount,
    DiscountsAndMultipliersService,
    SalesDocumentType,
    SalesDocument,
    SalesDocumentPosition,
    TaxRate,
    TaxRatesService,
} from '@icc/b2b/shared';
import { PriceBaseService2 } from 'libs/price2/src/lib/price-base.service';
import { Currency, ExchangeRateService } from '@icc/shared/currency';
import { core, isDefined } from '@icc/shared/helpers/b2b';
import { PriceSegment } from '@icc/shared/types';
import { DialogRef, DIALOG_DATA } from '@icc/ui';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SalesDocumentViewMode } from '../models/view-modes.model';
import { SalesDocumentService } from '../sales-document.service';
import { PriceDictionaryService } from '@icc/price2/b2b';
import { B2bDocumentPricesService } from '../b2b-document-prices.service';
import { ConfigService } from '@icc/shared';
import { Promotion } from 'libs/b2b/shared/src/lib/interfaces/promotion.interface';
import { isDepartmentIncludedInOrderIntegration } from '../order-integration-departments';

@Component({
    selector: 'icc-position-prices-dialog',
    templateUrl: './position-prices-dialog.component.html',
    styleUrls: ['./position-prices-dialog.component.scss'],
})
export class PositionPricesDialogComponent implements OnInit, OnDestroy {
    currencySymbol: string;
    discounts = [];

    position$ = this.salesDocumentService
        .getDocumentPositions$(this.data.salesDocument.uuid)
        .pipe(map((positions) => positions.find((el) => el.uuid === this.data.position.uuid)));
    buyPosition$ = this.salesDocumentService
        .getDocumentPositions$(this.data.buyPosition?.documentId)
        .pipe(map((positions) => positions.find((el) => el.uuid === this.data.buyPosition?.uuid)));

    position: SalesDocumentPosition;
    buyPosition: SalesDocumentPosition;

    priceSettings: {
        rasters?: any[],
        prices?: any[],
        marketFactor?: number,
        taxRate?: number,
        charge?: number,
        isGross?: boolean,
        multiplier?: number,
        priceVersionId?: number,
        contractorCompanyId?: number | null,
        promotion: Promotion | null,
    } = {
        rasters: [],
        prices: [],
        marketFactor: 1,
        charge: 1,
        taxRate: 0,
        isGross: false,
        multiplier: 1,
        priceVersionId: 0,
        contractorCompanyId: null,
        promotion: null,
    };
    priceSegments: PriceSegment[] = [];
    hasOrderIntegration = this.configService.user.orderIntegration === 'OrdersSynchronization'
        && (
            !this.configService.user.orderIntegrationDepartments?.length
            || isDepartmentIncludedInOrderIntegration(
                this.configService.user.orderIntegrationDepartments,
                this.data.salesDocument.sellerProductDepartmentId,
            )
        );

    valuationMode = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private salesDocumentService: SalesDocumentService,
        private exchangeRateService: ExchangeRateService,
        private taxRatesService: TaxRatesService,
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            salesDocument: SalesDocument;
            position: SalesDocumentPosition;
            buyPosition?: SalesDocumentPosition;
            currency: string;
            saleView: boolean;
            purchaseView: boolean;
            viewMode: SalesDocumentViewMode;
            isGross: boolean;
            isEditable: boolean;
            taxRates: TaxRate;
            name: string[];
        },
        private priceBaseService: PriceBaseService2,
        private b2bDocumentPricesService: B2bDocumentPricesService,
        private priceDictionaryService: PriceDictionaryService,
        private configService: ConfigService,
    ) {
        this.currencySymbol = this.exchangeRateService.getCurrencySymbol(data.currency);
    }

    close() {
        this.dialogRef.close();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.position$.subscribe((position) => {
                this.position = position;
                this.valuationMode = this.data.salesDocument.salesDocumentTypeId === SalesDocumentType.Inquiry;
                this.prepareDiscounts();
                this.setPriceSettings();
            }),
            this.buyPosition$.subscribe((buyPosition) => {
                this.buyPosition = buyPosition;
                this.prepareDiscounts();
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach((el) => el.unsubscribe());
    }

    changePositionTaxRate(taxRateId: number) {
        const taxRate = this.taxRatesService.getTaxRate(taxRateId);
        if (taxRate) {
            this.position.priceTaxRate = taxRate.value;
            this.position.priceTaxRateId = taxRateId;
            this.salesDocumentService.changePositionTaxRate(
                this.position,
                this.data.salesDocument,
                taxRateId
            );
        }
    }

    changePositionPrice(price: number) {
        this.salesDocumentService.changePositionPrice(
            this.position,
            this.data.salesDocument,
            price
        );
    }

    taxValue(tax: number, price: number) {
        return core.round((price * tax) / 100);
    }

    /**
     * Musimy obsłużyć przypadek, gdy dokument zakupu jest brutto, a sprzedaży netto.
     * W takim wypadku, aby dało się porównać ceny wszystko musi być netto.
     */
    convertToNetBuyPrice(price: number) {
        if (this.data.viewMode !== 'comparison' || this.position.priceIsGross || !this.buyPosition.priceIsGross) {
            return price;
        }
        return TaxRatesService.recalcToNetPrice(price, this.buyPosition.priceTaxRate);
    }

    prepareDiscounts() {
        this.discounts = [];
        if (this.data.saleView) {
            this.position.priceDiscounts.forEach((salesDiscount) => {
                this.discounts.push({
                    type: salesDiscount.type,
                    sales: salesDiscount,
                    buy: this.buyPosition?.priceDiscounts.find(
                        (buyDiscount) => buyDiscount.type === salesDiscount.type
                    ),
                });
            });
        }
        if (this.data.purchaseView) {
            this.buyPosition?.priceDiscounts.forEach((buyDiscount) => {
                if (!this.discounts.some((el) => el.buy?.type === buyDiscount.type)) {
                    this.discounts.push({
                        type: buyDiscount.type,
                        sales: null,
                        buy: buyDiscount,
                    });
                }
            });
        }
    }

    async changeDiscount(discount, type, value) {
        await this.salesDocumentService.changePositionDiscount(
            this.position,
            this.data.salesDocument,
            discount,
            type,
            value
        );
        this.prepareDiscounts();
    }

    setPriceSettings() {
        const configuration = core.parseJson(this.position.configuration);
        if (configuration.priceSegments) {
            const defaultTaxRate = this.taxRatesService.getDefaultTaxRate();
            this.priceSettings = {
                taxRate: defaultTaxRate?.value ? defaultTaxRate.value : 0,
                isGross: TaxRatesService.isGross(
                    this.taxRatesService.getTaxRateCalculationMethod()
                ),
                charge: 1,
                multiplier: 1,
                marketFactor: 1,
                priceVersionId: this.b2bDocumentPricesService.activePriceVersion?.id,
                contractorCompanyId: this.data.salesDocument.buyerCompanyId,
                promotion: null,
            };
            this.priceSegments = core.copy(configuration.priceSegments);
            this.priceSegments.forEach((segment: PriceSegment) => {
                if (segment.valueType === 'value') {
                    if (!isNaN(segment.baseValue)) {
                        segment.baseValue = TaxRatesService.recalcPrice(
                            this.priceSettings.isGross,
                            this.exchangeRateService.exchange(
                                segment.baseValue,
                                segment.valueCurrency,
                                this.position.priceCurrency,
                            ),
                            segment.valueIsGross,
                            segment.taxRate,
                            this.priceSettings.taxRate,
                        );
                        segment.valueIsGross = this.priceSettings.isGross ?? false;
                        segment.valueCurrency = this.position.priceCurrency;
                        segment.taxRate = this.priceSettings.taxRate;
                        segment.value = core.round(segment.baseValue * segment.quantity);
                    } else {
                        segment.valueIsGross = this.priceSettings.isGross ?? false;
                        segment.valueCurrency = this.position.priceCurrency;
                        segment.taxRate = this.priceSettings.taxRate;
                    }
                }

                if (segment.valueType === 'percent' && segment.baseValue != null) {
                    segment.baseValue = core.round((segment.baseValue * 100) - 100);
                    segment.value = core.round((segment.value * 100) - 100);
                }
                if (segment.valueType === 'value' && !segment.quantity) {
                    segment.quantity = 1;
                }
            });
        }
    }

    changeSegment(segment: PriceSegment, field: 'quantity' | 'baseValue', value: number) {
        segment[field] = value;
        if (field === 'baseValue') {
            segment.value = value;

            if (segment.valueType === 'value') {
                segment.value = core.round(segment.value * segment.quantity);
            }
        }
        const configuration = core.parseJson(this.position.configuration);
        configuration.priceSegments = this.priceSegments.map((el) => {
            if (el.id === segment.id) {
                el[field] = segment[field];
                if (field === 'baseValue') {
                    el.value = value;

                    if (el.valueType === 'value') {
                        el.value = core.round(el.value * el.quantity);
                    }
                }
            }
            if (el.valueType === 'percent') {
                return {
                    ...el,
                    value: (el.value + 100) / 100,
                    baseValue: (el.baseValue + 100) / 100,
                };
            }
            return {
                ...el
            };
        });
        this.position.configuration = configuration;
        this.priceBaseService.setPreCalculatedPrice(this.position.configuration, this.priceSettings);
        this.salesDocumentService.valuatePositionPrice(this.position, this.data.salesDocument);
    }

    getSegmentType(segment: PriceSegment) {
        if (Object.prototype.hasOwnProperty.call(this.priceDictionaryService.dictionary, segment.type)) {
            return this.priceDictionaryService.dictionary[segment.type];
        }
    }

    getSegmentName(segment: PriceSegment) {
        let segmentName = null;
        if (isDefined(segment.data.name)) {
            segmentName = segment.data.name;
        } else if (
            isDefined(segment.data.width)
            && isDefined(segment.data.height)
            && isDefined(segment.data.typesSymbol)
        ) {
            segmentName =
                segment.data.typesSymbol.join(', ') + ', ' + segment.data.width + '×' + segment.data.height;
        } else if (isDefined(segment.data.width) && isDefined(segment.data.height)) {
            segmentName = segment.data.width + '×' + segment.data.height;
        } else if (isDefined(segment.data.length)) {
            segmentName = segment.data.length;
        } else if (isDefined(segment.data.shape)) {
            segmentName = segment.data.shape;
        }

        return segmentName;
    }

    trackBySegmentId(index: number, segment: PriceSegment) {
        return segment.id;
    }

    trackByDiscountType(index: number, discount: Discount) {
        return discount.type;
    }
}
